/**
 * SAFECARE Act Policy Configuration
 * Defines all adjustable policy levers and their default values based on the Act
 */

import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';

export const defaultSafecareConfig = {
    // ========================================================================
    // BILL PARAMETERS (Statutory Values)
    // ========================================================================

    // Financing rates (Section 202-203)
    startYear: 2027,
    simulationYears: 10, // Default display range (Calculates full 34 years internally)

    // Financing rates (Section 202-203)
    employerRate: SAFECARE_BILL_PARAMETERS.revenue.employerRate,              // 8% of payroll (Section 202)
    baseIndividualRate: SAFECARE_BILL_PARAMETERS.revenue.individualBaseRate,       // 3.5% of income (Section 203)
    baseSelfEmployedRate: SAFECARE_BILL_PARAMETERS.revenue.selfEmployedBaseRate,     // 11.5% for self-employed (Section 202+203)

    // Surtax thresholds and rates (Section 203(c)(3))
    surtaxThresholds: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets.map(b => b.threshold),
    surtaxRates: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets.map(b => b.rate),

    // Cost Sharing (Section 204)
    annualCopaymentCap: SAFECARE_BILL_PARAMETERS.costSharing.annualCopayCapPerPerson,           // $750 per person per year
    avgCopay: 10,                      // $10 average copay (estimated statutory avg)

    // Provider Payment (Section 402/405)
    providerPaymentMultiplier: SAFECARE_BILL_PARAMETERS.providerPayment.paymentFloor,    // 100% of Medicare rates
    ruralHospitalProtection: 1.2,      // 120% for rural/critical access
    ruralSafetyNetUplift: 0.05,        // 5% extra for rural/safety-net

    // Enforcement & Implementation (Statutory Mandates)
    enforceTrustFundRules: 1,          // Full Sec 206 auto-adjustments
    enforceDrugCeilings: 1,            // Full Sec 405 compliance
    enforceIRP: 1,                     // Full IRP enforcement
    enforceAdminSimplification: 1,     // Full admin uniform standards
    enforceTuitionCaps: 1,             // Full tuition cap enforcement
    enforceDebtRedemption: 1,          // Full debt redemption program
    enforceReceivershipProtections: 1, // Full receivership powers
    enforceMalpracticeReform: 1,       // Full malpractice reform

    // Spending / Appropriations (Statutory Limits)
    debtRedemptionBudget: SAFECARE_BILL_PARAMETERS.medicalDebt.annualFundingMin,         // $5B/year (Title VII)
    capitalGrantsAnnual: SAFECARE_BILL_PARAMETERS.safetyNet.annualCapitalGrantAuthorization, // $100B/year (Sec 604)
    smallInnovatorBudget: SAFECARE_BILL_PARAMETERS.smallInnovatorSupport.annualGrantAuthorization,         // $500M
    tuitionCapAnnual: SAFECARE_BILL_PARAMETERS.workforce.tuitionCaps.medicalDegree,           // $15,000 max tuition (Section 501)

    // Long-Term Care (LTSS) - Section 104(a)(13)
    ltssMultiplier: 1.0,               // Scale pipeline LTC (0.5 = half, 1.5 = +50%). Only applies when using pipeline data.

    // Trust Fund Rules (Section 206)
    initialTrustFundSeed: 0,           // Initial capital injection (User configurable 0 - 1.5T)
    reserveTargetMin: SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMin,            // 5% minimum (Sec 206(d))
    reserveTargetMax: SAFECARE_BILL_PARAMETERS.trustFund.reserveRatioMax,            // 10% maximum (Sec 206(e))
    generalFundPhaseDown: true,        // Section 207 phase-down
    generalFundPolicy: 'as_written',   // Strict Section 207 limits
    reserveFundCap: 1.50,              // Cap reserves at 150% of outlays (reduces Fed contrib if higher)

    // ========================================================================
    // MODEL SENSITIVITIES (Behavioral & Economic Assumptions)
    // ========================================================================

    // ========================================================================
    // MODEL SENSITIVITIES & BEHAVIORAL ASSUMPTIONS (New Sliders)
    // ========================================================================

    // 🏥 Provider Behavior
    providerParticipationRate: 1.0,    // 100% participation (If lower -> Cost Increase due to out-of-network)
    utilizationSurge: 0.15,            // 15% surge in utilization for newly insured (Years 1-3)
    providerConsolidationSpeed: 0.00,  // 0% consolidation rate (Prices are statutory, so market power doesn't raise rates)

    // 💼 Revenue Realism
    taxEvasionRate: 0.12,              // 12% tax evasion/gap
    payrollBaseErosion: 0.02,          // 2% annual erosion of payroll base (gig economy shift)
    surtaxAvoidance: 0.20,             // 20% elasticity. (Even with AGI, rich avoid realization via borrowing/corp retention)

    // 🌍 External Shocks
    drugInnovationReduction: 0.20,     // 20% reduction in new drug entry (Years 7+)
    medicalTourismLeakage: 0.03,       // 3% of wealthy seek care abroad (Revenue loss)
    // Default should represent "as written" implementation timing.
    // Use presets/sliders to stress-test injunction/litigation delays.
    legalChallengeDelay: 0,            // years of implementation delay due to lawsuits

    // 📊 Economic Sensitivity
    adminEfficiencyFloor: 0.045,       // Floor for admin costs (4.5% instead of 2%)
    wageGrowthDifferential: 0.005,     // +0.5% wage growth due to lower health premiums

    // 💊 Drug Pricing (Sec 405) - coarse scenario controls
    // 'bill': uses min(DVB, 110% IRP) at an aggregate level
    // 'irp_only': assumes IRP is enforceable and controlling
    // 'dvb_only': assumes IRP is invalidated and only domestic value benchmark applies
    drugPricingMode: 'bill',
    // Empirical anchors (coarse): international net prices are often far below US list/net prices.
    // These are modeled as price-as-%-of-US, not savings.
    internationalPricePctOfUS: 0.40,  // OECD-style anchor (US ≈ 2.5x international ⇒ 40%)
    domesticValuePricePctOfUS: 0.65,  // ICER-style/value-based anchor (placeholder; stress-testable)

    // Political-economy stress tests for the automatic stabilizer (Sec 206)
    // If false, the simulator will NOT apply automatic upward steps even when reserve ratio < 5%.
    allowAutoIncrease: true,
    // If false, the simulator will NOT apply automatic downward steps even when reserve ratio > 10%.
    allowAutoDecrease: true,

    // 🔬 Quality/Innovation Trade-offs
    providerQualityDecline: 0.05,      // 5% increase in complications due to staffing
    ruralHospitalClosureRate: 0.08,    // 8% closure rate per 5 years

    // Hidden Parameters Exposed
    providerPaymentFloor: SAFECARE_BILL_PARAMETERS.providerPayment.paymentFloor,         // Payment floor vs Medicare
    ruralUplift: 1.20,                 // Rural payment multiplier
    surtaxBracket1: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets[0].threshold,
    surtaxBracket2: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets[1].threshold,
    surtaxBracket3: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets[2].threshold,
    surtaxRate1: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets[0].rate,
    surtaxRate2: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets[1].rate,
    surtaxRate3: SAFECARE_BILL_PARAMETERS.revenue.surtaxBrackets[2].rate,

    // Scenario Assumptions ("As Written" = Neutral/Full Compliance)
    wageCoverage: 1.0,                 // 100% Tax Compliance (superseded by taxEvasionRate but kept for calc compatibility)
    selfEmploymentCoverage: 1.0,       // 100% Tax Compliance
    passThroughCoverage: 1.0,          // 100% Tax Compliance
    capitalCoverage: 1.0,              // 100% Capital Gains (AGI definition)
    retirementExemptionLimit: 100000,  // $100k retirement exemption (Section 203(b)(1)(B)(iv))
    nonWageCap: 0,                     // No cap

    // System Efficiency
    stateCooperation: 1.0,             // 100% implementation (Federal backstop)
    aiFraudIntensity: 1.0,             // 100% effectiveness of anti-fraud measures
    adminWasteReductionMax: 0.20,      // Max potential admin savings realized
    drugPriceReductionMax: 0.25,       // Max potential drug savings realized (vs baseline)
    tuitionReductionMax: 0.80,         // Max tuition reduction realized
    malpracticeReductionMax: 0.025,    // Max malpractice savings realized

    // Behavioral Responses
    supplementalCoverageRate: 0.25,    // 25% buy supplemental plans (Assumption)
    providerPaymentStringency: 1.0,    // 100% adherence to rates (No leakage)

    // Timing
    // (startYear is defined above in statutory parameters; do not duplicate keys)
    initialIntegrationShare: 0.90,     // Speed of federal program integration
    integrationRampYears: 7,           // Years to full integration

    // Workforce Transition
    severanceMonths: 6,                // Assumption
    retrainingFundingLevel: 'medium',  // Assumption
    tuitionSupportCoverage: 0.80,      // Assumption (Restored)
    serviceObligationStrength: 0.5,    // Assumption (Restored)
    tuitionCapCoverage: 1.0,           // Assumption
    reciprocalApprovalSpeed: 'expedited', // Assumption

    // NEW: User-requested tax thresholds
    // "200% PL" implies roughly $30k for single (2024 FPL $15,060 * 2 = $30,120).
    // We'll set a default "Standard Deduction" equivalent.
    incomeExemptionThreshold: 30120,   // Income below ~30k is tax-free

    // Investment Income Threshold (NIIT-style)
    // Capital income below this Adjusted Gross Income is exempt from the 3.5% tax
    investmentTaxThreshold: 0,         // $0 (All capital income is taxed by default)
};

/**
 * Get a copy of the default configuration
 */
export function getDefaultConfig() {
    return { ...defaultSafecareConfig };
}

/**
 * Level 1 Scenario: The Great Stagnation (Austerity & Compliance Failure)
 */
export function getGreatStagnationConfig() {
    const config = getDefaultConfig();
    config.employerRate = 0.06;          // Reduced payroll tax (political pressure)
    config.taxEvasionRate = 0.20;        // High evasion (low trust)
    config.providerPaymentMultiplier = 0.95; // 95% of Medicare (severe austerity)
    config.adminEfficiencyFloor = 0.06;  // Stuck at 6% admin overhead (inefficiency)
    config.drugPricingMode = 'dvb_only'; // IRP invalidated by courts
    return config;
}

/**
 * Validate configuration values
 */
export function validateConfig(config) {
    const errors = [];

    if (config.employerRate < 0 || config.employerRate > 0.20) {
        errors.push('Employer rate must be between 0% and 20%');
    }

    if (config.baseIndividualRate < 0 || config.baseIndividualRate > 0.10) {
        errors.push('Individual rate must be between 0% and 10%');
    }

    // Enforcement toggles should be 0, 0.5, or 1
    const enforcementKeys = Object.keys(config).filter(k => k.startsWith('enforce'));
    enforcementKeys.forEach(key => {
        const val = config[key];
        if (![0, 0.5, 1].includes(val)) {
            errors.push(`${key} must be 0, 0.5, or 1`);
        }
    });

    return errors;
}
